import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Stack, TextField, Typography, List, ListItem, ListItemText } from '@mui/material';

export default function ImportWarehousePackagesPanel({ importWarehousePackages }) {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const handleImport = async () => {
    setError('');
    setSummary(null);
    setLoading(true);

    try {
      const result = await importWarehousePackages(search.trim());

      if (!result.success) {
        setError(result.message || 'Import failed.');
        return;
      }

      setSummary(result);
    } catch (err) {
      setError(err && err.message ? err.message : 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Fetch packages from the warehouse API and add any that are not already in the database. Packages
        without a mailbox number are skipped.
      </Typography>

      <TextField
        label="Search (optional)"
        placeholder="Filter by tracking number or other warehouse search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        disabled={loading}
      />

      <LoadingButton
        variant="contained"
        size="large"
        loading={loading}
        onClick={handleImport}
        disabled={loading}
      >
        Fetch &amp; Import Packages
      </LoadingButton>

      {error && (
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      )}

      {summary && (
        <Alert severity="success" variant="filled">
          Found <strong>{summary.found}</strong> package{summary.found === 1 ? '' : 's'}.{' '}
          <strong>{summary.added}</strong> added, <strong>{summary.alreadyExists}</strong> already in database,{' '}
          <strong>{summary.skippedNoMailbox}</strong> skipped (no mailbox), <strong>{summary.failed}</strong> failed.
        </Alert>
      )}

      {summary && summary.errors && summary.errors.length > 0 && (
        <Alert severity="warning" variant="outlined">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Failed imports (showing up to {summary.errors.length})
          </Typography>
          <List dense disablePadding>
            {summary.errors.map((item) => (
              <ListItem key={`${item.tracking}-${item.reason}`} disableGutters>
                <ListItemText
                  primary={item.tracking || '(no tracking)'}
                  secondary={item.reason}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </Stack>
  );
}
